import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { AlertTriangle, X } from "lucide-react";
import { buildReservationCart } from "./billing-reservation";

const reasonLabel = (reason) =>
  reason && reason !== "insufficient_stock" ? ` (${reason.replace(/_/g, " ")})` : "";

// Keeps the bill's stock reservation in step with its items: any add, edit or
// delete of a line (whichever part of the screen made it) sends the whole cart.
// Items the server couldn't reserve are listed above the footer until the
// cashier reduces or removes them. It floats so the table's height is left
// alone; it can be closed and comes back with the next answer.
const BillingReservationSync = ({ reservation, isAutoBatch }) => {
  const { values } = useFormikContext();
  const [state, setState] = useState(() => reservation?.getState());
  const [closedState, setClosedState] = useState(null);

  useEffect(() => {
    if (!reservation) return undefined;
    setState(reservation.getState());
    return reservation.subscribe(setState);
  }, [reservation]);

  const cart = useMemo(
    () => buildReservationCart(values?.data, { isAutoBatch }),
    [values?.data, isAutoBatch]
  );

  useEffect(() => {
    reservation?.sync(cart);
  }, [reservation, cart]);

  if (!state || state.status === "idle" || closedState === state) return null;

  const isConflict = state.status === "conflict";
  const itemNames = new Map(
    (values?.data ?? []).map((row) => [row?.item_id, row?.item_name])
  );
  const unavailableItems = state.unavailableItems ?? [];
  let hint = `${state.errorMessage} It will be tried again when the bill changes or is saved.`;
  if (isConflict) {
    hint =
      unavailableItems.length > 0
        ? "Reduce or remove these items — the bill can't be saved until they're corrected."
        : state.errorMessage || "Check the bill's items and save again.";
  }

  return (
    <div
      role="alert"
      className="fixed right-6 bottom-[8.75rem] z-[55] w-[28rem] max-w-[calc(100vw-3rem)] rounded-lg border border-[#FECDCA] bg-[#FEF3F2] px-4 py-3 text-sm text-[#B42318] shadow-[0_4px_12px_rgba(16,24,40,0.12)]"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-[2px] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">
            {isConflict
              ? "Some items can't be reserved"
              : "Stock couldn't be reserved for this bill"}
          </div>
          {isConflict && unavailableItems.length > 0 && (
            <ul className="mt-1 max-h-[7rem] overflow-y-auto list-disc pl-4">
              {unavailableItems.map((item) => (
                <li
                  key={`${item?.item_id}|${item?.item_batch_number}|${item?.item_rack_location}`}
                >
                  <span className="font-medium">
                    {itemNames.get(item?.item_id) ?? item?.item_id}
                  </span>{" "}
                  — batch {item?.item_batch_number || "-"}, rack{" "}
                  {item?.item_rack_location || "-"}: requested{" "}
                  {item?.requested_qty}, available {item?.available_qty}
                  {reasonLabel(item?.reason)}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1">{hint}</div>
        </div>
        <button
          type="button"
          onClick={() => setClosedState(state)}
          aria-label="Close"
          className="shrink-0 rounded p-[2px] hover:bg-[#FEE4E2]"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default BillingReservationSync;
